import { Formik, FormikProps } from "formik";
import React, { FunctionComponent, memo } from "react";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { VALIDATION_TYPES } from "@/components/Formik";
import { MEDIA_SIZE_XS } from "@/core/media";
import { ICompany } from "@/data/companies";
import { initialMapPhone } from "@/data/phones";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { IPhoneFormProps, IPhoneFormValues, PhoneForm } from "@/views/components/Forms";
import { Messenger } from "@/views/components/Messenger";

import { ICompanyFormikProps } from "../types";

export const COMPANY_PHONE_NUMBER_FORM_MESSENGER_CONTEXT = "company-phone-form";

export const validationCompanyPhoneNumberSectionSchema = Yup.object().shape({
  phone: Yup.object().shape(
    {
      phoneNumber: Yup.string()
        .required(VALIDATION_TYPES.REQUIRED)
        .test({
          message: VALIDATION_TYPES.NUMBERS,
          test: (phone: any) => !phone || !isNaN(phone),
        }),
      phoneCode: Yup.string()
        .required(VALIDATION_TYPES.REQUIRED)
        .when("phoneNumber", {
          is: (val: any) => !!val,
          then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
          otherwise: Yup.string().nullable(true),
        }),
    },
    [["phoneCode", "phoneNumber"]],
  ),
});

interface ICompanyPhoneNumberFormProps extends IPhoneFormProps, IEditableSectionRenderProps {
  source: ICompany;
  onCancel: () => void;
}

export const CompanyPhoneForm: FunctionComponent<ICompanyPhoneNumberFormProps> = memo(props => {
  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);

  useEditableSubmit(props.form, props.editableProps);

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md" margin={[isVerySmallScreen ? "lg" : false, false, false, false]}>
        <Messenger context={COMPANY_PHONE_NUMBER_FORM_MESSENGER_CONTEXT} />

        <Flex innerMargin="md" grow={1} direction={isVerySmallScreen ? "column" : "row"}>
          <Flex direction="column" width={{ xxs: 12, xs: 5 }}>
            <PhoneForm form={props.form} location={props.source.location} />
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
});

interface ICompanyPhoneNumberFormikProps extends ICompanyFormikProps<IPhoneFormValues>, ICompanyPhoneNumberFormProps {}

export const CompanyPhoneNumberFormik: FunctionComponent<ICompanyPhoneNumberFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={{
        ...initialValues,
        phone: initialMapPhone(initialValues.phone),
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<IPhoneFormValues>) => <CompanyPhoneForm {...rest} form={form} />}
    />
  ),
);
